import { useEffect, useState } from "react";
import { getUserArticlesAPI, reviewUserArticleAPI } from "../../services/api";
import getDecodeDescription from "../../services/decodeText";
import styles from "./CheckPublications.module.scss";

function previewText(html) {
  return String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 280);
}

const CheckPublications = () => {
  const [items, setItems] = useState([]);
  const [openIds, setOpenIds] = useState({});
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    const response = await getUserArticlesAPI();
    if (!response?.data) {
      setError("Could not load publications.");
      setItems([]);
      return;
    }
    setError("");
    setItems(response.data.items || []);
  };

  useEffect(() => {
    load();
  }, []);

  const review = async (item, action) => {
    if (action === "approve") {
      const ok = window.confirm(
        "Approve only after PayPal payment is received. This publishes the article live."
      );
      if (!ok) return;
    }
    setBusyId(item._id);
    setError("");
    try {
      const response = await reviewUserArticleAPI({
        id: item._id,
        type: item.type,
        action,
      });
      if (!response || response.status >= 400) {
        throw new Error(response?.data?.error || "Review failed");
      }
      await load();
    } catch (err) {
      setError(err?.message || "Review failed");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Check New Publications</h1>
      <p className={styles.note}>
        User submissions from the website. Approve only after payment.
      </p>
      {error && <p className={styles.error}>{error}</p>}
      {!items.length && <p>No pending publications.</p>}
      {items.map((item) => {
        const body = getDecodeDescription(item.body || "");
        const open = Boolean(openIds[item._id]);
        const preview = previewText(body);
        return (
          <article className={styles.card} key={item._id}>
            <div className={styles.meta}>
              <span className={styles.type}>{item.type}</span>
              <span>{item.status}</span>
              <span>{item.submittedByEmail}</span>
              {item.authorName ? <span>Byline: {item.authorName}</span> : null}
              <span>{item.country}</span>
              <span>{item.field}</span>
            </div>
            <div className={styles.heading}>
              <h2 className={styles.cardTitle}>{item.title}</h2>
              <button
                type="button"
                className={styles.toggle}
                aria-expanded={open}
                aria-label={open ? "Hide full article" : "Show full article"}
                onClick={() =>
                  setOpenIds((current) => ({
                    ...current,
                    [item._id]: !current[item._id],
                  }))
                }
              >
                <svg
                  className={open ? styles.chevronOpen : styles.chevron}
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <polyline
                    points="6 9 12 15 18 9"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
            {open ? (
              <>
                {item.photo ? (
                  <figure className={styles.photoBlock}>
                    <img className={styles.photo} src={item.photo} alt="" />
                    <figcaption className={styles.photoSource}>
                      {item.photoSource
                        ? `Photo: ${item.photoSource}`
                        : "Submitted photo"}
                      {" · "}
                      <a href={item.photo} target="_blank" rel="noreferrer">
                        Open full image
                      </a>
                    </figcaption>
                  </figure>
                ) : (
                  <p className={styles.photoSource}>No photo was submitted.</p>
                )}
                <div
                  className={styles.body}
                  dangerouslySetInnerHTML={{ __html: body }}
                />
              </>
            ) : (
              <p className={styles.preview}>{preview}</p>
            )}
            {item.status === "pending_review" && (
              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.approve}
                  disabled={busyId === item._id}
                  onClick={() => review(item, "approve")}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className={styles.reject}
                  disabled={busyId === item._id}
                  onClick={() => review(item, "reject")}
                >
                  Reject
                </button>
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
};

export default CheckPublications;
